/* eslint-disable max-lines */
import { NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
  ViewContainerRef,
} from '@angular/core';
import { Router } from '@angular/router';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of as observableOf, Subject } from 'rxjs';
import { distinctUntilChanged, map, takeUntil, tap, withLatestFrom } from 'rxjs/operators';

import { AccountSelectors } from '@recruitee/account';
import { CurrentUserSelectors } from '@recruitee/account/data';
import { Analytics } from '@recruitee/analytics';
import { filterBoolean, trackByProp } from '@recruitee/common';
import { FeatureGuardModule, GuardedFeature } from '@recruitee/feature-guard';
import { Company, CurrentUser, InterviewSchedule, User } from '@recruitee/http/types';
import { I18nModule, I18nService } from '@recruitee/i18n';
import {
  InterviewSchedulesCollectionActions,
  InterviewSchedulesCollectionScopes,
  InterviewSchedulesCollectionSelectors,
  InterviewSchedulesLinkEntityActions,
} from '@recruitee/interview-schedules/data';
import {
  AdminsAvatarsModule,
  BadgeModule,
  ButtonComponent,
  DialogRef,
  DialogService,
  DialogWidth,
  DividerModule,
  DropdownModule,
  EmptyStateModule,
  IconButtonComponent,
  IconComponent,
  KeyCode,
  ProtectedControlsDialogComponent,
  RtStatefulComponent,
  SpinnerModule,
  TruncateModule,
} from '@recruitee/user-interface';
import { includesWithTrim } from '@recruitee/utils';

import { ManageSchedulerLinkDialogComponentToken } from '../../../manage-scheduler-link-dialog-injection-token';
import { SchedulerLinkDropdownInterface } from '../../../redactor/types';
import { RedactorInsertsStep } from '../../../wysiwyg-extensions/link/redactor-inserts-dialog/types';
import { WysiwygLink, WysiwygLinkType } from '../../../wysiwyg-extensions/link/wysiwyg-link.types';
import { RedactorInsertsItemModule } from '../../redactor-inserts-item.module';

type MappedSchedule = {
  id: number;
  admins: User[];
  name: string;
  token: string;
};

enum InterviewScheduleGroupName {
  My = 'my',
  Team = 'team',
}

type InterviewScheduleGroup = {
  groupName: string;
  groupedSchedules: InterviewSchedule[];
};

type State = {
  searchQuery: string;
  schedules: InterviewSchedule[];
  scheduleGroups: InterviewScheduleGroup[];
  loaded: boolean;
  selectedId: number | null;
  hasFocus: boolean;
};

@Component({
  standalone: true,
  selector: 'rt-redactor-inserts-scheduler',
  templateUrl: 'redactor-inserts-scheduler.component.html',
  styleUrls: ['./redactor-inserts-scheduler.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    NgFor,
    ButtonComponent,
    IconComponent,
    IconButtonComponent,
    DividerModule,
    BadgeModule,
    TruncateModule,
    AdminsAvatarsModule,
    DropdownModule,
    SpinnerModule,
    I18nModule,
    EmptyStateModule,
    FeatureGuardModule,
    RedactorInsertsItemModule,
  ],
})
export class RedactorInsertsSchedulerComponent
  extends RtStatefulComponent<State>
  implements SchedulerLinkDropdownInterface
{
  @Input() public step: RedactorInsertsStep;
  @Input() public hasBackAction: boolean = true;
  @Input() public selected: boolean = false;

  @Output() public stepInside: EventEmitter<void> = new EventEmitter();
  @Output() public back: EventEmitter<void> = new EventEmitter();
  @Output() public settings: EventEmitter<string> = new EventEmitter();
  @Output() public closeDropdown: EventEmitter<void> = new EventEmitter();
  @Output() public insertLink: EventEmitter<WysiwygLink> = new EventEmitter();

  @ViewChildren('eleRef', { read: ElementRef })
  public items: QueryList<ElementRef>;

  public guardedFeatures: typeof GuardedFeature = GuardedFeature;
  public groupsTrackByFn = trackByProp<InterviewScheduleGroup>({ prop: 'groupName' });
  @ViewChildren('container') private containerList: QueryList<ElementRef>;

  private currentAdmin: CurrentUser;
  private currentCompany: Company;
  private ngOnDestroy$ = new Subject<void>();
  private readonly groupNames: Record<InterviewScheduleGroupName, string> = {
    my: this.i18n.translate('app.redactor_inserts.scheduler.dropdown.my_links'),
    team: this.i18n.translate('app.redactor_inserts.scheduler.dropdown.team_links'),
  };

  private manageSchedulerLinkDialogComponent = inject(ManageSchedulerLinkDialogComponentToken);

  constructor(
    protected cdRef: ChangeDetectorRef,
    private router: Router,
    private analytics: Analytics,
    private i18n: I18nService,
    private dialogService: DialogService,
    private viewRef: ViewContainerRef,
    private store: Store,
    private actions$: Actions,
  ) {
    super(cdRef);
    this.createState({
      loaded: false,
      searchQuery: '',
      schedules: [],
      scheduleGroups: [],
      selectedId: null,
      hasFocus: false,
    });
  }

  // eslint-disable-next-line max-lines-per-function
  public ngOnInit(): void {
    this.cdRef.detectChanges();
    this.loadScheduleCollection();

    this.store
      .select(AccountSelectors.currentCompany)
      .pipe(
        filterBoolean,
        withLatestFrom(this.store.select(CurrentUserSelectors.data)),
        takeUntil(this.ngOnDestroy$.asObservable()),
      )
      .subscribe(([currentCompany, currentAdmin]) => {
        this.currentCompany = currentCompany;
        this.currentAdmin = currentAdmin!;
      });

    this.store
      .select(
        InterviewSchedulesCollectionSelectors.entitiesWithReferences({
          scope: InterviewSchedulesCollectionScopes.Available,
        }),
      )
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(schedules => {
        const scheduleGroups = this.groupAndFilter(schedules, this.state.searchQuery);

        this.setState({
          ...this.state,
          schedules,
          scheduleGroups,
          selectedId: null,
        });
      });

    this.store
      .select(
        InterviewSchedulesCollectionSelectors.isPending({
          scope: InterviewSchedulesCollectionScopes.Available,
        }),
      )
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe((isPending: boolean) => {
        this.setState({
          ...this.state,
          loaded: !isPending,
        });
      });

    this.state$
      .pipe(
        map(state => state.selectedId),
        distinctUntilChanged(),
        takeUntil(this.ngOnDestroy$.asObservable()),
      )
      .subscribe(selectedId => {
        this.items.forEach(item => {
          const nativeElement = item.nativeElement;
          if (nativeElement) {
            const id = parseInt(nativeElement.getAttribute('data-id'), 10);
            if (id === selectedId) {
              nativeElement.scrollIntoView();
            }
          }
        });
      });

    this.actions$
      .pipe(
        ofType(InterviewSchedulesLinkEntityActions.updateSuccess),
        tap(() => this.loadScheduleCollection()),
        takeUntil(this.ngOnDestroy$.asObservable()),
      )
      .subscribe(({ payload }) => {
        const host = this.currentCompany!.host;
        const url = `https://${host}/v/i/s/${payload.token}/no-token`;

        this.insertLink.emit({
          type: WysiwygLinkType.Scheduler,
          url: url,
          label: url,
          name: payload.name,
          id: payload.token,
        });

        this.closeDropdown.emit();
      });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const { step } = changes;

    if (
      step &&
      step.currentValue &&
      step.currentValue === 'schedules' &&
      step.previousValue === 'insertion-list'
    ) {
      this.containerList.changes
        .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
        .subscribe((containerList: QueryList<ElementRef>) => {
          if (containerList.first && this.state.schedules.length < 6) {
            containerList.first.nativeElement.focus();
          }
        });
    }
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public goSecondStep(): void {
    this.stepInside.emit();
  }

  public goBack(): void {
    this.back.emit();
  }
  public goToSettings(url: string): void {
    this.settings.emit(url);
  }

  public onSearchQueryChange(searchQuery: string): void {
    this.setState({
      ...this.state,
      scheduleGroups: this.groupAndFilter(this.state.schedules, searchQuery),
      searchQuery,
    });
  }

  public trackBySchedule(index: number, schedule: MappedSchedule): number {
    return schedule.id;
  }

  public isStep(insertName: RedactorInsertsStep): boolean {
    return this.step === insertName;
  }

  public onScheduleSelect(schedule: MappedSchedule): void {
    const host = this.currentCompany!.host;
    const url = `https://${host}/v/i/s/${schedule.token}/no-token`;

    this.insertLink.emit({
      type: WysiwygLinkType.Scheduler,
      url: url,
      label: url,
      name: schedule.name,
      id: schedule.token,
    });

    this.closeDropdown.emit();
    this.analytics.track('[Mailbox] SchedulerLinkInserted');
  }

  public goToPage(link: string): void {
    this.router.navigate([link]);
  }

  public onFocusChanges(hasFocus: boolean): void {
    this.setState({
      ...this.state,
      hasFocus,
    });
  }

  // eslint-disable-next-line max-lines-per-function
  public handleKeyDown(event: KeyboardEvent): void {
    const key = event.which || event.keyCode;

    if (!this.isStep('schedules')) {
      return;
    }
    switch (key) {
      case KeyCode.Enter: {
        event.preventDefault();
        if (this.state.selectedId === null) {
          return;
        } else {
          this.onScheduleSelect(
            this.flatFilteredGroups(this.state.scheduleGroups).find(
              s => s.id === this.state.selectedId,
            )!,
          );
        }
        break;
      }

      case KeyCode.Backspace:
        if ((!this.state.hasFocus || this.state.searchQuery.length === 0) && this.hasBackAction) {
          event.preventDefault();
          this.back.emit();
        }
        break;

      case KeyCode.UpArrow: {
        event.preventDefault();
        const flattenTemplates = this.flatFilteredGroups(this.state.scheduleGroups);
        const index = flattenTemplates.findIndex(t => t.id === this.state.selectedId);

        if (index <= 0) {
          this.setState({
            ...this.state,
            selectedId: flattenTemplates[flattenTemplates.length - 1].id,
          });
        } else {
          this.setState({ ...this.state, selectedId: flattenTemplates[index - 1].id });
        }
        break;
      }

      case KeyCode.DownArrow: {
        event.preventDefault();
        const flattenTemplates = this.flatFilteredGroups(this.state.scheduleGroups);
        const index = flattenTemplates.findIndex(t => t.id === this.state.selectedId);

        if (index === flattenTemplates.length - 1 || index === -1) {
          this.setState({
            ...this.state,
            selectedId: flattenTemplates[0].id,
          });
        } else {
          this.setState({ ...this.state, selectedId: flattenTemplates[index + 1].id });
        }
        break;
      }

      default:
        return;
    }
  }

  public createSchedulerLink(event: MouseEvent): void {
    event.stopPropagation();
    this.openScheduleDialog();
  }

  public editSchedulerLink(event: MouseEvent, linkId: number): void {
    event.stopPropagation();
    this.openScheduleDialog(linkId);
  }

  private loadScheduleCollection(): void {
    this.store.dispatch(
      InterviewSchedulesCollectionActions.fetchCollection({
        payload: { scope: InterviewSchedulesCollectionScopes.Available },
      }),
    );
  }

  private duplicateSchedule(scheduleLinkId: number | null = null): void {
    this.openScheduleDialog(scheduleLinkId, true);
  }

  private openScheduleDialog(
    scheduleLinkId: number | null = null,
    isDuplicating: boolean = false,
  ): void {
    const dialog: DialogRef<any> = this.dialogService.openCustom(
      this.manageSchedulerLinkDialogComponent,
      {
        width: DialogWidth.Medium,
        closeGuard: () => {
          const { protectedControls } = dialog.componentInstance;
          if (protectedControls.locked) {
            return this.dialogService.openCustom(
              ProtectedControlsDialogComponent,
              {},
              {
                records: protectedControls.reports,
              },
              this.viewRef,
            ).afterClosed;
          } else {
            // eslint-disable-next-line max-lines
            return observableOf(true);
          }
        },
      },
      { scheduleLinkId, isEditing: scheduleLinkId !== null && !isDuplicating, isDuplicating },
      this.viewRef,
    );

    dialog.afterClosed.subscribe((value: string) => {
      if (value === 'duplicate') {
        this.duplicateSchedule(scheduleLinkId);
      }
    });
  }

  private flatFilteredGroups(schedules: InterviewScheduleGroup[]): InterviewSchedule[] {
    return schedules.reduce(
      (acc, group) => acc.concat(group.groupedSchedules),
      <InterviewSchedule[]>[],
    );
  }

  private groupAndFilter(
    schedules: InterviewSchedule[],
    searchQuery: string,
  ): InterviewScheduleGroup[] {
    const grouped = schedules
      .filter(schedule => includesWithTrim(schedule.name || '', searchQuery))
      .reduce((acc, schedule) => {
        const key = schedule.adminIds.includes(this.currentAdmin.id)
          ? InterviewScheduleGroupName.My
          : InterviewScheduleGroupName.Team;
        const group = acc[key] || [];
        acc[key] = [...group, schedule];
        return acc;
      }, <Record<string, InterviewSchedule[]>>{});

    return Object.keys(this.groupNames).reduce((acc, key: InterviewScheduleGroupName) => {
      if (grouped[key]?.length) {
        return [
          ...acc,
          {
            groupName: this.groupNames[key],
            groupedSchedules: grouped[key],
          },
        ];
      }

      return acc;
    }, []);
  }
}
